import { useMutation } from '@apollo/client/react'
import { type FeatureFlagKeyEnum } from '@ztechapps/common.contract'
import { FeatureFlagChoiceMapping } from '@ztechapps/common.contract'
import { useAppSettingValue } from '../useAppSettings'
import { setSettingMutation } from '../useAppSettings/setSettingMutation'

/**
 * Result type for the useFeatureFlag hook.
 *
 * @typeParam T - The string literal type representing valid choice values (e.g., 'TREE' | 'LIST')
 */
export type UseFeatureFlagResult<T extends string = string> = {
  /** The currently selected choice value for the feature flag */
  selectedChoice: T
  /** Async function to set a new choice for this feature flag */
  setChoice: (choice: T) => Promise<void>
  /** Whether the feature flag data is currently loading */
  loading: boolean
  /** Any error that occurred while fetching or setting the choice */
  error: any
  /** Array of valid choice values for this feature flag */
  choices: readonly T[]
  /** Metadata (name, description) for each choice value */
  metadata: Record<T, { name: string; description: string }>
}

/**
 * Hook to access and manage feature flag choices.
 *
 * @param key - The feature flag key to query
 * @returns Object containing selectedChoice, setChoice, loading, error, choices, and metadata
 *
 * @example
 * const { selectedChoice, setChoice, loading, choices, metadata } = useFeatureFlag(FeatureFlagKeyEnum.CUSTOMER_LAYOUT)
 *
 * if (selectedChoice === CustomerLayoutChoiceEnum.TREE) {
 *   return <TreeViewLayout />
 * }
 */
export function useFeatureFlag<T extends string = string>(key: FeatureFlagKeyEnum): UseFeatureFlagResult<T> {
  const config = FeatureFlagChoiceMapping[key]
  const { value, loading, error } = useAppSettingValue({ appSettingKey: key })

  const [setAppSettingMutation] = useMutation(setSettingMutation)

  // Get value from setting, fallback to default if no value or invalid value
  const selectedChoice = (value && config.choices.includes(value as T) ? value : config.defaultChoice) as T

  const setChoice = async (choice: T) => {
    if (!config.choices.includes(choice)) {
      throw new Error(`Invalid choice: ${choice}`)
    }
    await setAppSettingMutation({ variables: { input: { key, value: choice } } })
  }

  return {
    selectedChoice,
    setChoice,
    loading,
    error,
    choices: config.choices as readonly T[],
    metadata: config.metadata as Record<T, { name: string; description: string }>,
  }
}
